import { useCallback, useState } from 'react'
import { Link } from 'wouter'
import { Trans, useLingui } from '@lingui/react/macro'
import { Mail01Icon, GoogleIcon, UserIcon } from '@hugeicons/core-solid-rounded'

import {
  isErrorReturn,
  isSuccessReturn,
  Provider
} from '@guestgoodie/auth-core'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import Input from '@/components/ui/Input'
import { useAuth } from '../hooks/useAuth'
import { useErrorToText } from '../hooks/useErrorCode'
import VerificationCodeForm from './VerificationCodeForm'
import { PasswordField } from './PasswordField'

type FormData = {
  email: string
  password: string
  confirmPassword: string
  givenName: string
  lastName: string
  acceptToS: boolean
  turnstile: string
}

export default function SignUp() {
  const { t } = useLingui()
  const { client, refresh } = useAuth()
  const getError = useErrorToText()

  const [verification, setVerification] = useState<FormData | undefined>()
  const [serverError, setServerError] = useState<Form.ServerError>()

  const submit = useCallback(
    async (data: FormData) => {
      const response = await client.signUp({
        email: data.email,
        given_name: data.givenName,
        last_name: data.lastName,
        password: data.password,
        confirm_password: data.confirmPassword,
        turnstile: data.turnstile
      })

      if (isErrorReturn(response)) {
        if (response.errorCode === 'email_verification_needed') {
          setVerification(data)

          return
        }

        setServerError({
          message: getError(response.errorCode),
          type: response.errorCode
        })

        return
      }

      if (isSuccessReturn(response)) {
        refresh()

        return
      }
    },
    [client, getError, refresh]
  )

  if (verification) {
    return (
      <VerificationCodeForm
        email={verification.email}
        password={verification.password}
        rememberMe={false}
      />
    )
  }

  return (
    <>
      <Form.Root
        onSubmit={submit}
        serverErrors={
          serverError
            ? {
                root: { serverError }
              }
            : undefined
        }
        defaultValues={{
          givenName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          acceptToS: false
        }}
      >
        <div className="flex flex-col gap-4 px-2.5">
          <h1 className="text-text text-heading1 text-center">
            <Trans>Create Account</Trans>
          </h1>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-stretch w-full gap-2.5">
              <Form.Field
                name="givenName"
                label={t`Given Name`}
                rules={{
                  validate: {
                    required: Form.Validations.requiredWithTrimValidation(
                      t`This field is required`
                    )
                  }
                }}
              >
                {({ renderProps: { onChange, ...props } }) => (
                  <Input
                    type="text"
                    Icon={UserIcon}
                    onChange={event => onChange(event.target.value)}
                    placeholder=""
                    autoComplete="given-name webauthn"
                    {...props}
                  />
                )}
              </Form.Field>
              <Form.Field
                name="lastName"
                label={t`Family Name`}
                rules={{
                  validate: {
                    required: Form.Validations.requiredWithTrimValidation(
                      t`This field is required`
                    )
                  }
                }}
              >
                {({ renderProps: { onChange, ...props } }) => (
                  <Input
                    type="text"
                    onChange={event => onChange(event.target.value)}
                    placeholder=""
                    autoComplete="family-name webauthn"
                    {...props}
                  />
                )}
              </Form.Field>
            </div>
            <Form.Field
              name="email"
              label={t`Email`}
              rules={{
                validate: {
                  emailValidation: Form.Validations.emailValidation(
                    t`Not a valid email.`
                  ),
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="text"
                  Icon={Mail01Icon}
                  onChange={event => onChange(event.target.value)}
                  placeholder=""
                  autoComplete="email webauthn work"
                  {...props}
                />
              )}
            </Form.Field>
            <PasswordField />
            <Form.Field name="acceptToS">
              {({ renderProps: { onChange, value, ...props } }) => (
                <Checkbox
                  checked={value}
                  onChange={() => onChange(() => !value)}
                  {...props}
                >
                  <span>
                    <Trans>
                      I have read and agree to GuestGoodies{' '}
                      <a
                        className="text-secondary underline"
                        href="/toc"
                        target="_blank"
                      >
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        className="text-secondary underline"
                        href="/privacy"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>
                      .
                    </Trans>
                  </span>
                </Checkbox>
              )}
            </Form.Field>
            {import.meta.env.PROD && <Form.Turnstile name="turnstile" />}
            <div className="text-center">
              <Form.Error name="root.serverError" />
            </div>
            <div className="flex flex-col-reverse justify-evenly gap-4 p-2 md:flex-row">
              <Link to={`/sign-in${location.search ?? ''}`} asChild>
                <Button variant="outline" type="button" className="flex-1">
                  <Trans>I already have an Account</Trans>
                </Button>
              </Link>
              <Form.Submit className="flex-1">
                <Trans>Submit</Trans>
              </Form.Submit>
            </div>
          </div>
        </div>
      </Form.Root>
      <div className="w-full bg-text-light h-[2px] rounded my-4" />
      <div className="flex flex-col gap-1.5 p-2">
        <Button
          Icon={GoogleIcon}
          className="bg-secondary border-secondary hover:border-secondary-dark hover:bg-secondary-dark focus:outline-primary"
          onClick={() => client.signUpWithProvider(Provider.GOOGLE)}
        >
          <Trans>Sign up with Google</Trans>
        </Button>
      </div>
    </>
  )
}
